import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import messages from "../../../../assets/locale/messages";
import { UICard, UIPaginationBar } from "../../../ui";
import { showSuccessMsg, showWarningMsg } from "../../../../utils/Helpers";
import EventPayrollBulkActions from "./EventPayrollBulkActions";
import EventPayrollEmptyState from "./EventPayrollEmptyState";
import EventPayrollFullShiftToggle from "./EventPayrollFullShiftToggle";
import EventPayrollHeader from "./EventPayrollHeader";
import EventPayrollManualAdjustmentModal from "./EventPayrollManualAdjustmentModal";
import EventPayrollTable from "./EventPayrollTable";
import EventPayrollToolbar from "./EventPayrollToolbar";
import {
  isPortalEventCancelled,
  isPortalEventCompleted,
  isPortalEventOngoing,
  readEventStatus,
} from "../../../../utils/companyPortal/eventEditRules";
import EventPayrollAttachMarkPaidModal from "./EventPayrollAttachMarkPaidModal";
import EventPayrollViewProofModal from "./EventPayrollViewProofModal";
import {
  EventPayrollGeneratePayrollPage,
  useEventPayrollGeneratePayroll,
} from "./GeneratePayroll";
import {
  PAYROLL_URL_PARAMS,
  haveSamePayrollApplicantIds,
  normalizePayrollApplicantIds,
  parsePayrollGenerateIds,
  serializePayrollGenerateIds,
} from "./GeneratePayroll/constants/payrollUrlParams";
import { PAYROLL_GENERATE_ALL } from "./GeneratePayroll/utils/mapPayrollReportData";
import { exportPayrollReport } from "./GeneratePayroll/utils/exportPayrollReport";
import {
  bulkUpdateAdminPayrollAdjustments,
  bulkUpdateCompanyPayrollAdjustments,
  updateAdminPayrollAdjustment,
  updateCompanyPayrollAdjustment,
} from "../../../../network/apis/payrollAdjustments";
import {
  getAdminPayrolls,
  getCompanyPayrolls,
  logAdminPayrollExport,
  logCompanyPayrollExport,
} from "../../../../network/apis/payroll";
import { buildPayrollExportLogBody } from "./GeneratePayroll/utils/payrollExportLog";
import useEventPayroll from "./hooks/useEventPayroll";
import {
  PAYROLL_ADJUSTMENT_STATUSES,
  PAYROLL_EMPTY_STATE_VARIANTS,
  isMonthlyPayrollEvent,
} from "./constants";
import useEventPayrollAttachMarkPaid from "./hooks/useEventPayrollAttachMarkPaid";
import useEventPayrollAutoDeduction from "./hooks/useEventPayrollAutoDeduction";
import useEventPayrollCalculationSettings from "./hooks/useEventPayrollCalculationSettings";
import useEventPayrollManualAdjustment from "./hooks/useEventPayrollManualAdjustment";

import "./_EventPayrollTab.scss";

const EventPayrollTab = ({
  event,
  className = "event-payroll-tab",
  canAdd = true,
  canEdit = true,
  portal = "admin",
  onNestedViewChange,
  onRefreshEvent,
  companyLogoUrl,
}) => {
  const history = useHistory();
  const location = useLocation();
  const lang = useSelector((state) => state.locale.lang);
  const { events, shared } = messages[lang];
  const labels = events.viewEvents.payrollTab;

  // Read once, up front, so a remount (e.g. a language switch) can seed the hook's
  // initial state directly from the deep-link URL instead of opening closed and
  // reactively reopening after paint (which flashes the list first).
  const payrollGenerateIdsFromUrl = useMemo(
    () =>
      parsePayrollGenerateIds(
        new URLSearchParams(location.search).get(PAYROLL_URL_PARAMS.generate),
      ),
    [location.search],
  );

  const generatePayroll = useEventPayrollGeneratePayroll({
    event,
    eventId: event?.id,
    portal,
    lang,
    autoDeductionReason: labels.generatePayroll.sections.autoDeductionReason,
    initialApplicantIds: payrollGenerateIdsFromUrl,
  });

  const payroll = useEventPayroll({
    event,
    portal,
    lang,
    sharedMessages: shared,
    labels,
    enabled: !generatePayroll.open,
  });

  useEffect(() => {
    generatePayroll.setListFilterParams(payroll.filterParams);
  }, [payroll.filterParams]);

  const manualAdjustmentRecords = useMemo(() => {
    if (generatePayroll.data?.mode !== "single") return payroll.allRows;

    const { applicant, displayName } = generatePayroll.data;
    return [...payroll.allRows, { id: applicant.id, name: displayName }];
  }, [generatePayroll.data, payroll.allRows]);

  const adjustment = useEventPayrollManualAdjustment({
    records: manualAdjustmentRecords,
    lang,
  });

  const attachMarkPaid = useEventPayrollAttachMarkPaid({
    records: manualAdjustmentRecords,
    lang,
  });

  const [givingAllRewards, setGivingAllRewards] = useState(false);
  const [isRewardAllMode, setIsRewardAllMode] = useState(false);

  const handleCloseAdjustment = useCallback(() => {
    setIsRewardAllMode(false);
    adjustment.close();
  }, [adjustment]);

  const handleGiveAllRewards = useCallback(async () => {
    if (!event?.id || givingAllRewards) return;

    setGivingAllRewards(true);
    try {
      const getPayrolls = portal === "company" ? getCompanyPayrolls : getAdminPayrolls;
      const response = await getPayrolls({
        eventId: event.id,
        params: { page: -1, event_request_status: "approved" },
      });
      const approvedIds = (response?.data?.data?.payrolls || []).map(
        (row) => row.id,
      );

      if (approvedIds.length) {
        setIsRewardAllMode(true);
        adjustment.openReward(approvedIds);
      }
    } catch {
      // Interceptors surface the error message.
    } finally {
      setGivingAllRewards(false);
    }
  }, [adjustment, event?.id, givingAllRewards, portal]);

  const [viewProof, setViewProof] = useState({
    open: false,
    fileName: "",
    href: "",
  });

  const handleViewProof = useCallback((fileName, href) => {
    setViewProof({ open: true, fileName, href });
  }, []);

  const handleCloseViewProof = useCallback(() => {
    setViewProof((current) => ({ ...current, open: false }));
  }, []);

  const updatePayrollAdjustment =
    portal === "company" ? updateCompanyPayrollAdjustment : updateAdminPayrollAdjustment;
  const bulkUpdatePayrollAdjustments =
    portal === "company"
      ? bulkUpdateCompanyPayrollAdjustments
      : bulkUpdateAdminPayrollAdjustments;

  const isMonthly = isMonthlyPayrollEvent(event);
  const eventStatus = readEventStatus(event);
  const isEventCompleted = isPortalEventCompleted(eventStatus);

  const autoDeduction = useEventPayrollAutoDeduction({
    event,
    portal,
    successMessage: labels.toast.autoDeduction,
    canEdit,
    onSuccess: () => payroll.refresh(),
  });

  const calculationSettings = useEventPayrollCalculationSettings({
    event,
    portal,
    successMessage: labels.toast.payFullShift,
    canEdit,
    // Re-sync the parent event too, so a later remount doesn't seed the toggle from a stale prop.
    onSuccess: () => {
      payroll.refresh();
      onRefreshEvent?.({ silent: true });
    },
  });

  // The auto-deduction flags live on the event object, which is fetched once when
  // the event details page mounts — not on every Payroll tab visit. Refetch here so
  // reopening the tab after toggling (or after another tab session updated it)
  // reflects the current server state instead of the stale prop from last mount.
  useEffect(() => {
    onRefreshEvent?.({ silent: true });
  }, []);

  // Past / cancelled events reject the update (422), so the toggles stay read-only there.
  const isAutoDeductionDisabled =
    !canEdit ||
    autoDeduction.saving ||
    isEventCompleted ||
    isPortalEventCancelled(eventStatus);

  // Editable on ongoing events only; upcoming / completed / cancelled show it read-only.
  const isPayFullShiftDisabled =
    calculationSettings.saving || !isPortalEventOngoing(eventStatus);

  const selectedLabel = useMemo(
    () =>
      labels.selectedCount.replace("{count}", String(payroll.selectedIds.length)),
    [labels.selectedCount, payroll.selectedIds.length],
  );

  const showEmptyState = payroll.total === 0;
  const emptyVariant = payroll.hasActiveSearchOrFilter
    ? PAYROLL_EMPTY_STATE_VARIANTS.filtered
    : PAYROLL_EMPTY_STATE_VARIANTS.event;

  const handleAttachMarkPaidSuccess = useCallback(() => {
    payroll.clearSelection();
    payroll.refresh();
  }, [payroll]);

  const handleAdjustmentSuccess = useCallback(() => {
    payroll.clearSelection();
    payroll.refresh();
    generatePayroll.reload();
  }, [payroll, generatePayroll]);

  const handleRevertAllEntries = useCallback(
    async (entries, successMessage) => {
      const revertibleIds = (entries || [])
        .filter((entry) => entry.canRevert)
        .map((entry) => entry.id);
      if (!revertibleIds.length) return;

      try {
        const response = await bulkUpdatePayrollAdjustments({
          eventId: event?.id,
          params: {
            payroll_adjustment_ids: revertibleIds,
            status: PAYROLL_ADJUSTMENT_STATUSES.reverted,
          },
        });

        if (response?.status === 207) {
          showWarningMsg(response?.data?.message || successMessage);
        } else {
          showSuccessMsg(response?.data?.message || successMessage, true);
        }        generatePayroll.reload();
        payroll.refresh();
      } catch {
        // Interceptors surface the error message.
      }
    },
    [bulkUpdatePayrollAdjustments, event?.id, generatePayroll, payroll],
  );

  const handleRevertAllDeductions = useCallback(
    () =>
      handleRevertAllEntries(
        generatePayroll.data?.deductions?.entries,
        labels.generatePayroll.toast.revertDeduction,
      ),
    [generatePayroll.data, handleRevertAllEntries, labels.generatePayroll.toast.revertDeduction],
  );

  const handleRevertAllRewards = useCallback(
    () =>
      handleRevertAllEntries(
        generatePayroll.data?.rewards?.entries,
        labels.generatePayroll.toast.revertReward,
      ),
    [generatePayroll.data, handleRevertAllEntries, labels.generatePayroll.toast.revertReward],
  );

  const handleRevertEntry = useCallback(
    async (entry, successMessage) => {
      if (!entry?.id || !entry.canRevert) return;

      try {
        await updatePayrollAdjustment({
          eventId: event?.id,
          id: entry.id,
          params: { status: PAYROLL_ADJUSTMENT_STATUSES.reverted },
        });
        showSuccessMsg(successMessage, true);
        generatePayroll.reload();
        payroll.refresh();
      } catch {
        // Interceptors surface the error message.
      }
    },
    [event?.id, generatePayroll, payroll, updatePayrollAdjustment],
  );

  const handleRevertDeductionEntry = useCallback(
    (entry) => handleRevertEntry(entry, labels.generatePayroll.toast.revertDeductionEntry),
    [handleRevertEntry, labels.generatePayroll.toast.revertDeductionEntry],
  );

  const handleRevertRewardEntry = useCallback(
    (entry) => handleRevertEntry(entry, labels.generatePayroll.toast.revertRewardEntry),
    [handleRevertEntry, labels.generatePayroll.toast.revertRewardEntry],
  );

  const handleGeneratePayrollSuccess = useCallback(() => {
    payroll.clearSelection();
  }, [payroll]);

  const handleExportReport = useCallback(
    async (selectedFields, fields) => {
      if (!generatePayroll.data) return;

      try {
        await exportPayrollReport({
          data: generatePayroll.data,
          isSingle: generatePayroll.isSingle,
          selectedFields,
          fields,
          lang,
          eventName: event?.name,
          summaryLabels: labels.generatePayroll.summary,
          companyLogoUrl,
        });
        // Background activity-log call: never blocks or surfaces anything to the user.
        const logPayrollExport =
          portal === "company" ? logCompanyPayrollExport : logAdminPayrollExport;
        logPayrollExport({
          eventId: event?.id,
          body: buildPayrollExportLogBody({
            startDate: generatePayroll.startDate,
            endDate: generatePayroll.endDate,
            selectedFields,
            fields,
          }),
        }).catch(() => {});
        showSuccessMsg(labels.generatePayroll.toast.exportReport, false);
        handleGeneratePayrollSuccess();
      } catch {
        // Interceptors surface the error message.
      }
    },
    [
      companyLogoUrl,
      event?.id,
      event?.name,
      generatePayroll.data,
      generatePayroll.endDate,
      generatePayroll.isSingle,
      generatePayroll.startDate,
      handleGeneratePayrollSuccess,
      labels.generatePayroll.summary,
      labels.generatePayroll.toast.exportReport,
      lang,
      portal,
    ],
  );

  const updatePayrollGenerateUrl = useCallback(
    (applicantIds) => {
      const params = new URLSearchParams(location.search);
      const normalizedIds = normalizePayrollApplicantIds(applicantIds);
      const currentIds = parsePayrollGenerateIds(
        params.get(PAYROLL_URL_PARAMS.generate),
      );

      if (haveSamePayrollApplicantIds(currentIds, normalizedIds)) {
        if (normalizedIds.length && !generatePayroll.open) {
          generatePayroll.openGeneratePayroll(normalizedIds);
        }
        return;
      }

      if (!normalizedIds.length) {
        params.delete(PAYROLL_URL_PARAMS.generate);
      } else {
        params.set(
          PAYROLL_URL_PARAMS.generate,
          serializePayrollGenerateIds(normalizedIds),
        );
      }

      history.push({
        pathname: location.pathname,
        search: params.toString(),
      });
    },
    [generatePayroll, history, location.pathname, location.search],
  );

  const handleOpenGeneratePayroll = useCallback(
    (applicantIds) => {
      updatePayrollGenerateUrl(applicantIds);
    },
    [updatePayrollGenerateUrl],
  );

  const handleCloseGeneratePayroll = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete(PAYROLL_URL_PARAMS.generate);
    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  }, [history, location.pathname, location.search]);

  const handleGenerateAllPayroll = useCallback(() => {
    handleOpenGeneratePayroll([PAYROLL_GENERATE_ALL]);
  }, [handleOpenGeneratePayroll]);

  useEffect(() => {
    if (!payrollGenerateIdsFromUrl.length) {
      if (generatePayroll.open) {
        generatePayroll.close();
      }
      return;
    }

    if (
      !generatePayroll.open ||
      !haveSamePayrollApplicantIds(
        generatePayroll.applicantIds,
        payrollGenerateIdsFromUrl,
      )
    ) {
      generatePayroll.openGeneratePayroll(payrollGenerateIdsFromUrl);
    }
  }, [
    generatePayroll,
    payrollGenerateIdsFromUrl,
  ]);

  useEffect(() => {
    onNestedViewChange?.(generatePayroll.open);

    return () => {
      onNestedViewChange?.(false);
    };
  }, [generatePayroll.open, onNestedViewChange]);

  if (!event?.id && payroll.total === 0) {
    return null;
  }

  return (
    <div className={className}>
      {generatePayroll.open ? (
        <EventPayrollGeneratePayrollPage
          loading={generatePayroll.loading}
          data={generatePayroll.data}
          isSingle={generatePayroll.isSingle}
          applicantCount={generatePayroll.applicantCount}
          labels={labels.generatePayroll}
          shared={shared}
          startDate={generatePayroll.startDate}
          endDate={generatePayroll.endDate}
          canAdd={canAdd}
          canEdit={canEdit}
          onStartDateChange={generatePayroll.setStartDate}
          onEndDateChange={generatePayroll.setEndDate}
          onClose={handleCloseGeneratePayroll}
          onAddDeduction={() =>
            adjustment.openDeduction(generatePayroll.applicantIds)
          }
          onAddReward={() => adjustment.openReward(generatePayroll.applicantIds)}
          onRevertDeductionEntry={handleRevertDeductionEntry}
          onRevertRewardEntry={handleRevertRewardEntry}
          onRevertAllDeductions={handleRevertAllDeductions}
          onRevertAllRewards={handleRevertAllRewards}
          onExportReport={handleExportReport}
        />
      ) : (
        <div className="event-payroll">
          <EventPayrollHeader
            labels={labels}
            summaryLabels={labels.summary}
            stats={payroll.stats}
            isMonthly={isMonthly}
            lateCheckInDeduction={autoDeduction.lateCheckInDeduction}
            autoCheckout={autoDeduction.autoCheckout}
            canEdit={canEdit}
            autoDeductionDisabled={isAutoDeductionDisabled}
            onLateCheckInDeductionChange={autoDeduction.onLateCheckInDeductionChange}
            onAutoCheckoutChange={autoDeduction.onAutoCheckoutChange}
          >
            {!isMonthly && (
              <EventPayrollFullShiftToggle
                labels={labels.payFullShift}
                checked={calculationSettings.payFullShift}
                canEdit={canEdit}
                disabled={isPayFullShiftDisabled}
                onChange={calculationSettings.onPayFullShiftChange}
              />
            )}
          </EventPayrollHeader>
          <EventPayrollToolbar
            labels={labels}
            search={payroll.search}
            filters={payroll.filters}
            genderOptions={payroll.genderOptions}
            roleOptions={payroll.roleOptions}
            nationalityOptions={payroll.nationalityOptions}
            removedStatusOptions={payroll.removedStatusOptions}
            rewardTypeOptions={payroll.rewardTypeOptions}
            paymentStatusOptions={payroll.paymentStatusOptions}
            canAdd={canAdd}
            isGenerateAllDisabled={payroll.total === 0}
            isGiveAllRewardsDisabled={payroll.total === 0}
            isGiveAllRewardsLoading={givingAllRewards}
            onSearchChange={payroll.onSearchChange}
            onFilterChange={payroll.onFilterChange}
            onGenerateAllPayroll={handleGenerateAllPayroll}
            onGiveAllRewards={handleGiveAllRewards}
          />

          {showEmptyState ? (
            <UICard className="event-payroll__empty-card">
              <EventPayrollEmptyState variant={emptyVariant} labels={labels} />
            </UICard>
          ) : (
            <div className="event-payroll__table-shell">
              <EventPayrollBulkActions
                labels={labels}
                selectedCount={payroll.selectedIds.length}
                selectedLabel={selectedLabel}
                canAdd={canAdd}
                isEventCompleted={isEventCompleted}
                onAttachAndMarkPaid={() =>
                  attachMarkPaid.openAttachMarkPaid(payroll.selectedIds)
                }
                onGeneratePayroll={() =>
                  handleOpenGeneratePayroll(payroll.selectedIds)
                }
                onGiveReward={() => adjustment.openReward(payroll.selectedIds)}
                onManualDeduction={() =>
                  adjustment.openDeduction(payroll.selectedIds)
                }
              />
              <EventPayrollTable
                rows={payroll.rows}
                labels={labels}
                lang={lang}
                selectedIds={payroll.selectedIds}
                canAdd={canAdd}
                canEdit={canEdit}
                isEventCompleted={isEventCompleted}
                onSelectRow={payroll.onSelectRow}
                onSelectAll={payroll.onSelectAll}
                onAddProof={(id) => attachMarkPaid.openAttachMarkPaid([id])}
                onViewProof={handleViewProof}
                onRowClick={(row) => handleOpenGeneratePayroll([row.id])}
              />
              <UIPaginationBar
                currentPage={payroll.page}
                pages={payroll.pages}
                pageSize={payroll.pageSize}
                rangeTemplate={labels.rangeTemplate}
                rangeFrom={payroll.rangeFrom}
                rangeTo={payroll.rangeTo}
                rangeTotal={payroll.total}
                rowsPerPageLabel={labels.rowsPerPageLabel}
                onPageChange={payroll.onPageChange}
                onPageSizeChange={payroll.onPageSizeChange}
                className="event-payroll__pagination"
              />
            </div>
          )}
        </div>
      )}

      <EventPayrollAttachMarkPaidModal
        open={attachMarkPaid.open}
        applicantIds={attachMarkPaid.applicantIds}
        workerName={attachMarkPaid.workerName}
        eventId={event?.id}
        portal={portal}
        labels={labels.attachMarkPaid}
        shared={shared}
        singleSuccessMessage={labels.toast.addProof}
        isEventCompleted={isEventCompleted}
        onClose={attachMarkPaid.close}
        onSuccess={handleAttachMarkPaidSuccess}
      />

      <EventPayrollViewProofModal
        open={viewProof.open}
        fileName={viewProof.fileName}
        href={viewProof.href}
        labels={labels.viewProof}
        shared={shared}
        onClose={handleCloseViewProof}
      />

      <EventPayrollManualAdjustmentModal
        open={adjustment.open}
        type={adjustment.type}
        applicantIds={adjustment.applicantIds}
        workerName={adjustment.workerName}
        isGiveAll={isRewardAllMode}
        successMessage={isRewardAllMode ? labels.toast.giveAllRewards : undefined}
        eventId={event?.id}
        portal={portal}
        labels={labels.manualAdjustment}
        shared={shared}
        lang={lang}
        onClose={handleCloseAdjustment}
        onSuccess={handleAdjustmentSuccess}
      />
    </div>
  );
};

EventPayrollTab.propTypes = {
  event: PropTypes.object,
  className: PropTypes.string,
  canAdd: PropTypes.bool,
  canEdit: PropTypes.bool,
  portal: PropTypes.oneOf(["admin", "company"]),
  onNestedViewChange: PropTypes.func,
  onRefreshEvent: PropTypes.func,
  companyLogoUrl: PropTypes.string,
};

EventPayrollTab.defaultProps = {
  event: null,
  className: "event-payroll-tab",
  canAdd: true,
  canEdit: true,
  portal: "admin",
  onNestedViewChange: undefined,
  onRefreshEvent: undefined,
  companyLogoUrl: undefined,
};

export default EventPayrollTab;
